import { loginData } from '../client/apollo';
import { AchievementRarity, QuestMetricType } from '../client/generated/graphql';
import { getRandomColor } from '../helpers/color';
import { getRandomIcon } from '../helpers/common';
import { localTimeToUtcIso } from '../helpers/datetime';
import { resolveIconFromKeyword } from '../helpers/iconSemantics';
import type { VoiceAction } from '../types/voice';

export interface ActionWithCategory {
    categoryName?: string;
    categoryId?: string;
}

export interface CreateQuestAction extends ActionWithCategory {
    type: 'CREATE_QUEST';
    questName: string;
    dueDate?: string;
    difficulty?: string;
    focusModeEnabled?: boolean;
    timedIntervalMinutes?: number;
    repeatQuery?: string;
}

export interface CreateCategoryAction {
    type: 'CREATE_CATEGORY';
    categoryName: string;
    iconName?: string;
    iconColorName?: string;
}

export interface PostponeQuestAction {
    type: 'POSTPONE_QUEST';
    questName?: string;
    questId?: string;
    dueDate?: string;
}

export interface CreateAchievementAction extends ActionWithCategory {
    type: 'CREATE_ACHIEVEMENT';
    achievementName: string;
    questNames?: string[];
    questIds?: string[];
    conditionMetricType?: string;
    conditionTarget?: number;
    iconName?: string;
    dependsOnActionIndices?: number[];
    rarity?: string;
}

export type AssistantAction = CreateQuestAction | CreateCategoryAction | PostponeQuestAction | CreateAchievementAction;

const assistantUrl = () => process.env.REACT_APP_VOICE_ASSISTANT_URL!;

function resolveVoiceAchievementIcon(raw?: string): string {
    const t = raw?.trim();
    if (!t) return getRandomIcon();
    const fromKeyword = resolveIconFromKeyword(t);
    if (fromKeyword) return fromKeyword;
    return getRandomIcon();
}

const QUEST_METRIC_SET = new Set<string>(Object.values(QuestMetricType));
const ACHIEVEMENT_RARITY_SET = new Set<string>(Object.values(AchievementRarity));

function parseAchievementRarity(raw?: string): AchievementRarity {
    if (raw && ACHIEVEMENT_RARITY_SET.has(raw)) return raw as AchievementRarity;
    return AchievementRarity.Uncommon;
}

function parseConditionMetric(raw?: string): QuestMetricType {
    if (raw && QUEST_METRIC_SET.has(raw)) return raw as QuestMetricType;
    return QuestMetricType.Completion;
}

function normalizeConditionTarget(metric: QuestMetricType, raw?: number): number | null {
    if (metric === QuestMetricType.Completion) return null;
    if (raw != null && Number.isFinite(raw) && raw > 0) return raw;
    if (metric === QuestMetricType.Repeating) return 5;
    if (metric === QuestMetricType.Experience) return 100;
    return 1;
}

function voiceActionDisplayTitle(a: AssistantAction): string {
    switch (a.type) {
        case 'CREATE_CATEGORY':
            return a.categoryName;
        case 'POSTPONE_QUEST':
            return a.questName ?? '';
        case 'CREATE_ACHIEVEMENT':
            return a.achievementName ?? 'Achievement';
        case 'CREATE_QUEST':
            return a.questName;
    }
}

function resolveAchievementDependencies(
    actions: AssistantAction[],
    ids: string[],
    a: CreateAchievementAction,
): {
    dependsOnActionIds: string[];
    dependsOnActionIndices: number[];
    categoryId?: string;
    categoryName?: string;
} {
    let categoryId = a.categoryId;
    let categoryName = a.categoryName;
    const indices = a.dependsOnActionIndices ?? [];
    const dependsOnActionIds: string[] = [];
    const dependsOnActionIndices: number[] = [];

    const inheritCategory = categoryId == null && categoryName == null;
    const seen = new Set<number>();

    for (const depIdx of indices) {
        if (seen.has(depIdx)) continue;
        if (!Number.isFinite(depIdx) || depIdx < 0 || depIdx >= actions.length) continue;
        seen.add(depIdx);
        const parent = actions[depIdx];
        if (parent.type !== 'CREATE_QUEST') continue;
        dependsOnActionIndices.push(depIdx);
        dependsOnActionIds.push(ids[depIdx]);
        if (inheritCategory && dependsOnActionIds.length === 1) {
            categoryId = parent.categoryId ?? categoryId;
            categoryName = parent.categoryName ?? categoryName;
        }
    }

    return {
        dependsOnActionIds,
        dependsOnActionIndices,
        categoryId,
        categoryName,
    };
}

export function mapAssistantActionsToVoice(actions: AssistantAction[], userTimezone?: string): VoiceAction[] {
    const ids = actions.map(() => crypto.randomUUID());

    return actions.map((a, index): VoiceAction => {
        const base = {
            id: ids[index],
            displayTitle: voiceActionDisplayTitle(a),
            type: a.type,
        };
        if (a.type === 'CREATE_CATEGORY') {
            return {
                ...base,
                categoryName: a.categoryName,
                iconName: a.iconName ?? getRandomIcon(),
                iconColorName: a.iconColorName ?? getRandomColor(),
            } as VoiceAction;
        }
        if (a.type === 'POSTPONE_QUEST') {
            return {
                ...base,
                questName: a.questName ?? '',
                questId: a.questId,
                dueDateIso: a.dueDate ? localTimeToUtcIso(a.dueDate, userTimezone) : undefined,
            } as VoiceAction;
        }
        if (a.type === 'CREATE_ACHIEVEMENT') {
            const conditionMetricType = parseConditionMetric(a.conditionMetricType);
            const conditionTarget = normalizeConditionTarget(conditionMetricType, a.conditionTarget);
            const dep = resolveAchievementDependencies(actions, ids, a);
            return {
                ...base,
                achievementName: a.achievementName,
                categoryName: dep.categoryName,
                categoryId: dep.categoryId,
                questNames: a.questNames,
                questIds: a.questIds,
                icon: resolveVoiceAchievementIcon(a.iconName),
                rarity: parseAchievementRarity(a.rarity),
                conditionMetricType,
                conditionTarget,
                dependsOnActionIndices: dep.dependsOnActionIndices.length ? dep.dependsOnActionIndices : undefined,
                dependsOnActionIds: dep.dependsOnActionIds.length ? dep.dependsOnActionIds : undefined,
            } as VoiceAction;
        }
        return {
            ...base,
            questName: a.questName,
            categoryName: a.categoryName,
            categoryId: a.categoryId,
            dueDateIso: a.dueDate ? localTimeToUtcIso(a.dueDate, userTimezone) : undefined,
            difficulty: a.difficulty,
            focusModeEnabled: a.focusModeEnabled,
            timedIntervalMinutes: a.timedIntervalMinutes,
            repeatQuery: a.repeatQuery,
        } as VoiceAction;
    });
}

export async function getActionsFromTranscript(transcript: string, timezone?: string, language?: string): Promise<AssistantAction[]> {
    const token = loginData()?.value ?? '';
    const url = assistantUrl();
    const res = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ transcript, timezone, language }),
    });
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
    const data = await res.json();
    return data.actions ?? [];
}
